import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
    OnboardingLayout,
    OnboardingDataCard,
    EditInput,
    type OnboardingStep,
    type BadgeStatus,
} from "@vanyshr/ui/components/onboarding";
import { cx } from "@/utils/cx";
import { Phone, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { allowLocalRouteBypass } from "@/lib/env";
import { patchDevProgress } from "@/lib/dev-user";
import { loadLocalOnboardingSeed } from "@/lib/local-onboarding-seed";

/** Convert E.164 (+18162258592) → display format (816) 225-8592 */
function formatPhoneDisplay(number: string): string {
    const digits = number.replace(/\D/g, "").slice(-10);
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return number;
}

/** Auto-format phone digits to (XXX) XXX-XXXX as the user types */
function formatPhoneInput(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Normalize any user-typed phone number to E.164 (+1XXXXXXXXXX) */
function normalizeToE164(input: string): string {
    let digits = input.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    if (digits.length === 10) return `+1${digits}`;
    return digits; // fallback — store as-is if non-standard
}

export interface PhoneNumberItem {
    id: string;          // user_phones.id (UUID)
    number: string;
    status: BadgeStatus;
    isPrimary: boolean;
    isNew?: boolean;     // true while unsaved
}

export function OnboardingPhoneNumbers() {
    const [items, setItems] = useState<PhoneNumberItem[]>([]);
    const [profileId, setProfileId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState("");

    const navigate = useNavigate();

    // -----------------------------------------------------------------------
    // Load from DB
    // -----------------------------------------------------------------------
    useEffect(() => {
        async function load() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                if (allowLocalRouteBypass()) {
                    const seed = loadLocalOnboardingSeed();
                    setItems((seed?.phones ?? []).map((number, i) => ({
                        id: `local-phone-${i}`,
                        number,
                        status: "pending" as BadgeStatus,
                        isPrimary: i === 0,
                    })));
                }
                setIsLoading(false);
                return;
            }

            const { data: profile } = await supabase
                .from("user_profiles")
                .select("id")
                .eq("auth_user_id", user.id)
                .single();

            if (!profile) { setIsLoading(false); return; }
            setProfileId(profile.id);

            const { data: phones } = await supabase
                .from("user_phones")
                .select("id, number, is_primary, user_confirmed_status")
                .eq("user_id", profile.id)
                .eq("is_active", true)
                .order("is_primary", { ascending: false });

            if (phones) {
                setItems(phones.map((p) => ({
                    id:        p.id,
                    number:    formatPhoneDisplay(p.number),
                    isPrimary: p.is_primary,
                    status:    p.user_confirmed_status === "confirmed" ? "confirmed" : "pending" as BadgeStatus,
                })));
            }
            setIsLoading(false);
        }
        load();
    }, []);

    // -----------------------------------------------------------------------
    // DB helpers
    // -----------------------------------------------------------------------
    const upsertPhone = useCallback(async (item: PhoneNumberItem) => {
        if (!profileId) return;
        const now = new Date().toISOString();
        if (item.isNew) {
            const { data } = await supabase
                .from("user_phones")
                .insert({
                    user_id:               profileId,
                    number:                item.number,
                    is_primary:            item.isPrimary,
                    user_confirmed_status: "confirmed",
                    confirmed_at:          now,
                    source:                "user_input",
                })
                .select("id")
                .single();
            // Replace temp id with real DB id
            if (data) {
                setItems((prev) =>
                    prev.map((p) => p.id === item.id ? { ...p, id: data.id, isNew: false } : p)
                );
            }
        } else {
            await supabase
                .from("user_phones")
                .update({
                    number:                item.number,
                    is_primary:            item.isPrimary,
                    user_confirmed_status: "confirmed",
                    confirmed_at:          now,
                })
                .eq("id", item.id);
        }
    }, [profileId]);

    const deletePhone = useCallback(async (id: string, isNew?: boolean) => {
        if (isNew) return; // never saved — nothing to delete
        const { error } = await supabase
            .from("user_phones")
            .update({ is_active: false })
            .eq("id", id);
        if (error) console.error("[Onboarding] Phone delete failed:", error.message);
    }, []);

    const updateAllPrimary = useCallback(async (primaryId: string) => {
        if (!profileId) return;
        // Unset all primaries then set the new one
        await supabase
            .from("user_phones")
            .update({ is_primary: false })
            .eq("user_id", profileId);
        await supabase
            .from("user_phones")
            .update({ is_primary: true })
            .eq("id", primaryId);
    }, [profileId]);

    // -----------------------------------------------------------------------
    // Interaction handlers
    // -----------------------------------------------------------------------
    const openEdit = (item: PhoneNumberItem) => {
        setActiveId(item.id);
        setEditingId(item.id);
        setEditValue(formatPhoneInput(item.number));
    };

    const handleUpdate = async (id: string) => {
        const rawValue = editValue.trim();
        const normalized = rawValue ? normalizeToE164(rawValue) : null;
        const display = normalized ? formatPhoneDisplay(normalized) : null;

        const updated = items.map((p) =>
            p.id === id
                ? {
                      ...p,
                      number: display ?? p.number,
                      status: "confirmed" as BadgeStatus,
                  }
                : p,
        );
        setItems(updated);
        setEditingId(null);
        setActiveId(null);

        // Save E.164 to DB, not the display format
        const item = updated.find((p) => p.id === id);
        if (item) await upsertPhone({ ...item, number: normalized ?? item.number });
    };

    const handleTogglePrimary = async (id: string, isPrimary: boolean) => {
        setItems((prev) => prev.map((p) => ({ ...p, isPrimary: p.id === id ? isPrimary : false })));
        if (isPrimary) await updateAllPrimary(id);
    };

    const handleDelete = async (id: string) => {
        const item = items.find((p) => p.id === id);
        setItems((prev) => prev.filter((p) => p.id !== id));
        if (activeId === id) setActiveId(null);
        setEditingId(null);
        if (item) await deletePhone(id, item.isNew);
    };

    const handleAdd = () => {
        const tempId = `new-${Date.now()}`;
        const newItem: PhoneNumberItem = {
            id:        tempId,
            number:    "",
            status:    "pending",
            isPrimary: items.length === 0,
            isNew:     true,
        };
        setItems((prev) => [...prev, newItem]);
        setActiveId(tempId);
        setEditingId(tempId);
        setEditValue("");
    };

    const handleConfirmAndContinue = async () => {
        // Bulk-confirm all active items so none are left as 'unverified'
        if (profileId) {
            await supabase
                .from("user_phones")
                .update({
                    user_confirmed_status: "confirmed",
                    confirmed_at:          new Date().toISOString(),
                })
                .eq("user_id", profileId)
                .eq("is_active", true);
        }
        // Advance onboarding step
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            await supabase
                .from("user_profiles")
                .update({ onboarding_step: 3 })
                .eq("auth_user_id", user.id);
        }
        patchDevProgress({ step: 3 });
        setActiveId(null);
        setEditingId(null);
        navigate("/onboarding/aliases");
    };

    if (isLoading) {
        return (
            <OnboardingLayout
                currentStep={"phone" satisfies OnboardingStep}
                completedSteps={["basic", "emails"]}
                title="Phone Numbers"
                subtitle="Click on Field to Edit"
                onDashboardNavigate={() => navigate("/scanning-started")}
                footer={null}
            >
                <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 rounded-full border-2 border-[#14ABFE] border-t-transparent animate-spin" />
                </div>
            </OnboardingLayout>
        );
    }

    return (
        <OnboardingLayout
            currentStep={"phone" satisfies OnboardingStep}
            completedSteps={["basic", "emails"]}
            title="Phone Numbers"
            subtitle="Click on Field to Edit"
            onDashboardNavigate={() => navigate("/scanning-started")}
            footer={
                <div className="flex w-full gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/onboarding/emails")}
                        className={cx(
                            "flex h-[52px] flex-1 items-center justify-center rounded-xl text-sm font-semibold outline-none transition",
                            "border border-[var(--border-subtle)] dark:border-[#1E3A52]",
                            "text-[#0B1B2B] dark:text-white bg-transparent hover:bg-state-hover dark:hover:bg-white/10",
                            "focus-visible:ring-2 focus-visible:ring-[#14ABFE] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                        )}
                    >
                        &lt; BACK
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirmAndContinue}
                        className={cx(
                            "flex h-[52px] flex-1 items-center justify-center rounded-xl text-sm font-semibold text-white outline-none transition",
                            "bg-[#14ABFE] hover:bg-[#0E9AE8]",
                            "focus-visible:ring-2 focus-visible:ring-[#14ABFE] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                        )}
                    >
                        CONFIRM &gt;
                    </button>
                </div>
            }
        >
            {items.length === 0 && (
                <div className={cx(
                    "rounded-xl border p-6 text-center",
                    "bg-[var(--bg-surface)] dark:bg-[#112538]",
                    "border-[var(--border-subtle)] dark:border-[#1E3A52]",
                )}>
                    <p className="text-sm text-[var(--text-secondary)] dark:text-[#94A3B8] mb-4">
                        No phone numbers found from your scan. Add your phone numbers so we can find and remove them from data broker sites.
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {items.map((item) => (
                    <OnboardingDataCard
                        key={item.id}
                        value={item.number}
                        status={item.status}
                        isExpanded={activeId === item.id}
                        isEditing={editingId === item.id}
                        editContent={
                            editingId === item.id ? (
                                <EditInput
                                    id={`phone-${item.id}`}
                                    label="Phone number"
                                    value={editValue}
                                    onChange={(v) => setEditValue(formatPhoneInput(v))}
                                    placeholder="+1 (555) 000-0000"
                                    icon={Phone}
                                />
                            ) : undefined
                        }
                        onClick={() => activeId === item.id ? setActiveId(null) : openEdit(item)}
                        onConfirmAndContinue={() => handleUpdate(item.id)}
                        toggleLabel="Primary Mobile"
                        toggleValue={item.isPrimary}
                        onToggleChange={(v) => handleTogglePrimary(item.id, v)}
                        showDelete
                        onDelete={() => handleDelete(item.id)}
                    />
                ))}
            </div>

            <div className="mt-8 flex justify-center pb-4">
                <button
                    type="button"
                    onClick={handleAdd}
                    className={cx(
                        "flex h-14 w-14 items-center justify-center rounded-full outline-none transition shadow-lg",
                        "bg-[#00D4AA] text-[#0B1B2B] hover:bg-[#00E5B8]",
                        "focus-visible:ring-2 focus-visible:ring-[#00D4AA] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                    )}
                    aria-label="Add Phone Number"
                >
                    <Plus className="h-6 w-6" aria-hidden />
                </button>
            </div>
        </OnboardingLayout>
    );
}
